import { DialogoDeConfirmacionGlobal } from '@/components/ui/confirm-dialog';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { Sidebar } from './sidebar';
import { BarraPlataforma } from './barra-plataforma';
import { Navbar } from './navbar';
import { BugReportButton } from './bug-report-button';
import { SubscriptionBanner } from './subscription-banner';
import { SupportModeBanner } from './support-mode-banner';
import { CintaDeEntorno } from './cinta-de-entorno';
import { TourProvider } from '@/features/guided-tour/components/tour-provider';
import { MisionesDemo } from '@/features/mini-prixia/components/misiones-demo';
import { DialogoDelPlanCompletoGlobal } from '@/features/mini-prixia/components/dialogo-del-plan-completo';
import { OnboardingModal } from '@/features/onboarding/components/onboarding-modal';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useToast } from '@/lib/hooks/use-toast';
import { claveDeLaRuta } from '@/router/clave-de-ruta';
import { useTemaEnElDocumento } from '@/components/tema/tema-compite';

export type VarianteDelMarco = 'portal' | 'plataforma';

/** Lo que una pantalla le pide al marco. */
export interface PedidoDelMarco {
  variante: VarianteDelMarco;
  /** El editor del Pricer (F5): sin relleno de abajo ni scroll propio del contenido. */
  pantallaCompleta: boolean;
}

interface Marco {
  /** Una pantalla pide el marco (menú lateral, barra de arriba y botones flotantes). */
  pedir: (id: string, pedido: PedidoDelMarco) => void;
  soltar: (id: string) => void;
}

const MarcoContext = createContext<Marco | null>(null);

/** El marco de la aplicación, si la pantalla está adentro de uno. */
export function useMarcoDeLaApp() {
  return useContext(MarcoContext);
}

/**
 * Cuánto se sostiene el marco cuando ninguna pantalla lo pide: lo que tarda una
 * redirección (`<Navigate>`) en montar la pantalla a la que lleva.
 */
const RETENCION_MS = 200;

/**
 * El marco fijo de la aplicación: el menú lateral, la barra de arriba, los avisos
 * y los botones flotantes se arman UNA vez y quedan quietos; al navegar sólo
 * cambia el contenido.
 *
 * 🔴 Antes cada una de las ~70 pantallas dibujaba su propio `AppLayout`, y el
 * router además fundía la pantalla entera al cambiar de dirección. Al pasar de un
 * módulo a otro se desarmaba todo —menú incluido—, se veía blanco y se volvía a
 * armar: el menú perdía su scroll y volvía a pedir sus contadores. Con las
 * pantallas que se cargan aparte (las de Compite) eran casi 900 ms sin nada
 * (medido por `e2e/specs/navegacion-sin-parpadeo.spec.ts`). Pedido de Joaquín,
 * 16-oct.
 *
 * Cada pantalla sigue escribiendo `<AppLayout>`: adentro de este marco eso sólo
 * pide el marco y pone su contenido; fuera de él (pruebas, o una pantalla suelta)
 * `AppLayout` arma su propio marco, como siempre. Las páginas públicas (login,
 * landing, seguimiento) no lo piden y se dibujan sin él.
 */
export function MarcoDeLaApp({
  children,
  propio,
}: {
  children: ReactNode;
  /**
   * El marco que arma un `AppLayout` suelto (fuera del router, en una prueba):
   * dice de entrada si es pantalla completa, para la clave del contenido.
   */
  propio?: { pantallaCompleta: boolean };
}) {
  const [pedidos, setPedidos] = useState<Array<{ id: string } & PedidoDelMarco>>([]);
  const pedir = useCallback(
    (id: string, pedido: PedidoDelMarco) =>
      setPedidos((p) => [...p.filter((x) => x.id !== id), { id, ...pedido }]),
    []
  );
  const soltar = useCallback((id: string) => setPedidos((p) => p.filter((x) => x.id !== id)), []);
  const marco = useMemo(() => ({ pedir, soltar }), [pedir, soltar]);

  const autenticado = useAuthStore((s) => s.isAuthenticated);
  const quiere = pedidos.length > 0;
  const ultimoPedido = useRef<PedidoDelMarco>({ variante: 'portal', pantallaCompleta: false });
  if (quiere) ultimoPedido.current = pedidos[pedidos.length - 1];
  const { variante, pantallaCompleta } = ultimoPedido.current;

  /*
    Una redirección monta primero un `<Navigate>` (que no pide el marco) y en el
    cuadro siguiente la pantalla de destino. Soltarlo ahí hacía parpadear el
    menú: se sostiene un momento, y sólo con sesión (sin sesión el menú no tiene
    qué mostrar).
  */
  const [retenido, setRetenido] = useState(false);
  useLayoutEffect(() => {
    if (quiere) {
      setRetenido(true);
      return;
    }
    const t = window.setTimeout(() => setRetenido(false), RETENCION_MS);
    return () => window.clearTimeout(t);
  }, [quiere]);
  const activo = quiere || (retenido && autenticado);
  /*
    Rediseño del 18-oct: el tema (forma, letra y tamaños del artefacto) vale para
    toda la app, no sólo Compite. Los portales lo toman por `<html>`.
  */
  useTemaEnElDocumento(activo);

  // La pantalla nueva empieza arriba: el contenedor que scrollea ya no se rearma.
  const { pathname } = useLocation();
  /*
    Qué rearma el contenido. En el router, la dirección, salvo el editor del
    Pricer: de `/editor/new` a `/editor/:id` (al guardar) NO se rearma
    (`claveDeLaRuta`, F5 T5). Un marco propio de pantalla completa nunca.
  */
  const clave = propio
    ? propio.pantallaCompleta
      ? 'pantalla-completa'
      : pathname
    : claveDeLaRuta(pathname);
  const contenido = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (contenido.current) contenido.current.scrollTop = 0;
  }, [pathname]);

  // Errores 404 en operaciones de escritura (un dato que ya no existe).
  const { toast } = useToast();
  useEffect(() => {
    if (!activo) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      toast({
        variant: 'destructive',
        title: 'Recurso no encontrado',
        description:
          detail?.message || 'El registro fue eliminado o ya no existe. Recargando datos...',
      });
      // Forzar recarga de datos en la página actual
      window.dispatchEvent(new Event('api:refresh-data'));
    };
    window.addEventListener('api:stale-resource', handler);
    return () => window.removeEventListener('api:stale-resource', handler);
  }, [activo, toast]);

  /*
    El contenido ocupa SIEMPRE el mismo lugar del árbol (div › div › div), con o
    sin marco: así prender o apagar el marco no rearma la pantalla.
  */
  return (
    <MarcoContext.Provider value={marco}>
      {/* `fixed inset-0`: la app ocupa exactamente la ventana (main, 0825d08c). */}
      <div
        className={
          activo ? 'tema-compite fixed inset-0 flex overflow-clip bg-lienzo text-sm' : undefined
        }
      >
        {activo && (variante === 'plataforma' ? <BarraPlataforma /> : <Sidebar />)}

        <div className={activo ? 'flex-1 flex flex-col min-h-0 min-w-0 overflow-clip' : undefined}>
          {activo && (
            <>
              {/* Cinta de entorno: sólo fuera de PROD (vault 277, PD-30). */}
              <CintaDeEntorno />
              <SupportModeBanner />
              <SubscriptionBanner />
              <Navbar />
            </>
          )}

          {/*
            🔴 `pb-44` no es decoración: es el espacio de los tres botones
            flotantes de abajo a la derecha (asistente, reportar problema y
            ayuda), en `position: fixed`. Sin él la última fila de una tabla
            quedaba tapada. Medido: la banda que ocupan mide 168 px; `pb-44` son
            176 px. El editor del Pricer (pantalla completa) no lo lleva: cada
            columna scrollea sola.
          */}
          <div
            ref={contenido}
            data-marco-contenido=""
            className={
              activo
                ? pantallaCompleta
                  ? 'flex-1 min-h-0 overflow-clip'
                  : 'flex-1 overflow-y-auto pb-44'
                : undefined
            }
          >
            {/* Sólo el contenido se funde, rápido y sin pasar por blanco. */}
            <motion.div
              key={clave}
              initial={{ opacity: 0.35 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className={activo && pantallaCompleta ? 'h-full' : undefined}
            >
              {children}
            </motion.div>
          </div>
        </div>

        {activo && (
          <>
            <TourProvider />
            {/* Mini Prixia: sólo se dibujan para una empresa demo. */}
            <MisionesDemo />
            <DialogoDelPlanCompletoGlobal />
            <OnboardingModal />
            <BugReportButton />
          </>
        )}
        {/* El cuadro de `confirmar()`, para toda la app (en lugar del del navegador). */}
        <DialogoDeConfirmacionGlobal />
      </div>
    </MarcoContext.Provider>
  );
}
